import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { paymentAPI, coursesAPI } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle, Clock, ArrowRight } from 'lucide-react';
import { VideoCard } from '../components/VideoCard';
import { extractInstructor } from '../lib/utils';

export const PaymentSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id');
    
    const [status, setStatus] = useState('checking');
    const [paymentData, setPaymentData] = useState(null);
    const [error, setError] = useState('');
    const [pollCount, setPollCount] = useState(0);
    const [suggestedCourses, setSuggestedCourses] = useState([]);

    // Fetch upsell courses once payment is confirmed
    useEffect(() => {
        if (status !== 'success' || !paymentData) return;
        coursesAPI.getAll().then(res => {
            const all = res.data?.data || [];
            const purchasedId = paymentData.course_id || paymentData.video_id;
            const instructor = paymentData.instructor;
            // Exclude what they just bought; same instructor first
            const others = all.filter(c => c.course_id !== purchasedId);
            const sameInstructor = others.filter(c =>
                instructor && (c.instructor || extractInstructor(c.title || ''))?.toLowerCase() === instructor.toLowerCase()
            );
            const rest = others.filter(c =>
                !instructor || (c.instructor || extractInstructor(c.title || ''))?.toLowerCase() !== instructor.toLowerCase()
            );
            setSuggestedCourses([...sameInstructor, ...rest].slice(0, 3));
        }).catch(() => {});
    }, [status, paymentData]);

    useEffect(() => {
        if (!sessionId) {
            setStatus('error');
            setError('No session ID found');
            return;
        }

        const pollPaymentStatus = async () => {
            try {
                const response = await paymentAPI.getCheckoutStatus(sessionId);
                const data = response.data;
                setPaymentData(data);

                if (data.payment_status === 'paid') {
                    setStatus('success');
                    // GA4: track purchase event
                    if (typeof window.gtag === 'function') {
                        window.gtag('event', 'purchase', {
                            transaction_id: sessionId,
                            value: data.amount_total ? data.amount_total / 100 : 0,
                            currency: data.currency?.toUpperCase() || 'USD',
                        });
                    }
                } else if (data.status === 'expired') {
                    setStatus('expired');
                } else if (pollCount < 5) {
                    // Continue polling
                    setTimeout(() => {
                        setPollCount(prev => prev + 1);
                    }, 2000);
                } else {
                    setStatus('pending');
                }
            } catch (err) {
                console.error('Error checking payment status:', err);
                setError('Failed to verify payment status');
                setStatus('error');
            }
        };

        pollPaymentStatus();
    }, [sessionId, pollCount]);

    return (
        <div className="section-gap" data-testid="payment-success-page">
            <div className="container-main max-w-2xl mx-auto">
                <Card className="p-8 text-center max-w-lg mx-auto">
                    {status === 'checking' && (
                        <>
                            <Loader2 className="h-16 w-16 animate-spin text-accent mx-auto mb-4" />
                            <h1 className="font-serif text-2xl font-semibold mb-2">
                                Verifying Payment...
                            </h1>
                            <p className="text-muted-foreground">
                                Please wait while we confirm your payment.
                            </p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="h-12 w-12 text-green-600" />
                            </div>
                            <h1 className="font-serif text-2xl font-semibold mb-2 text-green-700">
                                Payment Successful!
                            </h1>
                            <p className="text-muted-foreground mb-6">
                                Thank you for your purchase. Your content is now available in your library.
                            </p>
                            {paymentData && (
                                <p className="text-sm text-muted-foreground mb-6">
                                    Amount: ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                                </p>
                            )}
                            <div className="flex flex-col gap-3">
                                <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90">
                                    <Link to="/my-library" data-testid="go-to-library">
                                        Go to My Library
                                    </Link>
                                </Button>
                                <Button asChild variant="outline">
                                    <Link to="/courses">
                                        Browse More Courses
                                    </Link>
                                </Button>
                            </div>
                        </>
                    )}

                    {status === 'pending' && (
                        <>
                            <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
                                <Clock className="h-12 w-12 text-yellow-600" />
                            </div>
                            <h1 className="font-serif text-2xl font-semibold mb-2 text-yellow-700">
                                Payment Processing
                            </h1>
                            <p className="text-muted-foreground mb-6">
                                Your payment is still being processed. This may take a few moments.
                                Please check your email for confirmation.
                            </p>
                            <Button asChild variant="outline">
                                <Link to="/my-library">
                                    Check My Library
                                </Link>
                            </Button>
                        </>
                    )}

                    {status === 'expired' && (
                        <>
                            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                                <XCircle className="h-12 w-12 text-gray-500" />
                            </div>
                            <h1 className="font-serif text-2xl font-semibold mb-2">
                                Session Expired
                            </h1>
                            <p className="text-muted-foreground mb-6">
                                Your payment session has expired. Please try again.
                            </p>
                            <Button asChild variant="outline">
                                <Link to="/courses">
                                    Back to Courses
                                </Link>
                            </Button>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                                <XCircle className="h-12 w-12 text-red-500" />
                            </div>
                            <h1 className="font-serif text-2xl font-semibold mb-2 text-red-700">
                                Something Went Wrong
                            </h1>
                            <p className="text-muted-foreground mb-6">
                                {error || 'We could not verify your payment. Please contact support.'}
                            </p>
                            <Button asChild variant="outline">
                                <Link to="/courses">
                                    Back to Courses
                                </Link>
                            </Button>
                        </>
                    )}
                </Card>

                {/* Post-purchase upsell — shown only after confirmed payment */}
                {status === 'success' && suggestedCourses.length > 0 && (
                    <div className="mt-12" data-testid="upsell-section">
                        <div className="text-center mb-6">
                            <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                                Continue learning
                            </span>
                            <h2 className="font-serif text-2xl font-medium text-primary mt-2">
                                {paymentData?.instructor
                                    ? `More from ${paymentData.instructor}`
                                    : 'You might also enjoy'}
                            </h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {suggestedCourses.map(course => (
                                <VideoCard
                                    key={course.course_id}
                                    video={{ ...course, id: course.course_id }}
                                    type="course"
                                    data-testid={`upsell-course-${course.course_id}`}
                                />
                            ))}
                        </div>
                        <div className="text-center mt-6">
                            <Link
                                to="/courses"
                                className="text-sm font-medium text-accent hover:underline inline-flex items-center gap-1"
                                data-testid="browse-all-courses"
                            >
                                Browse all courses <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentSuccessPage;
